import axios, { AxiosInstance, AxiosError } from 'axios';
import { store } from './state';
import { API_BASE_URL } from './config';
import type { Match, Chat, Message } from './state';
import type {
  ProfileJson,
  ProfilePhoto,
  CropData,
  FiltersJson,
  VisiJson,
  PreferencesUpdateData,
  SearchParams,
  SearchResult,
  BrowseProfile,
  MatchCheckResult,
  OwnProfileResponse,
  PublicProfileResponse,
  PreferencesResponse,
  VisibilityResponse,
  ChatDetailResponse,
  ChatCreateResponse,
  ChatImageUploadResponse,
  SentImage,
} from './types/api';

// Re-export types for backward compatibility
export type {
  ProfilePhoto,
  CropData,
  FiltersJson,
  VisiJson,
  PreferencesUpdateData,
  SearchParams,
  SearchResult,
  BrowseProfile,
  MatchCheckResult,
} from './types/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(config => {
  const state = store.getState();
  if (state.accessToken) {
    config.headers.Authorization = `Bearer ${state.accessToken}`;
  }
  return config;
});

// Response interceptor - handle token refresh
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const state = store.getState();
      if (state.refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
            refreshToken: state.refreshToken,
          });

          const { accessToken, refreshToken } = response.data;
          store.setTokens(accessToken, refreshToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          store.clearTokens();
          window.location.hash = '#/welcome';
        }
      } else {
        store.clearTokens();
        window.location.hash = '#/welcome';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  requestCode: (email: string) =>
    api.post<{ message: string; isNewUser: boolean }>('/api/auth/request-code', { email }),

  verify: (email: string, code: string) =>
    api.post<{ accessToken: string; refreshToken: string; isNewUser: boolean }>(
      '/api/auth/verify',
      { email, code }
    ),

  refresh: (refreshToken: string) =>
    api.post<{ accessToken: string; refreshToken: string }>(
      '/api/auth/refresh',
      { refreshToken }
    ),

  logout: () =>
    api.post<{ success: boolean }>('/api/auth/logout'),

  deleteAccount: () =>
    api.delete<{ success: boolean; message: string }>('/api/auth/account'),

  appleAuth: (data: {
    identityToken: string;
    user: string;
    email?: string | null;
    givenName?: string | null;
    familyName?: string | null;
  }) =>
    api.post<{ accessToken: string; refreshToken: string; isNewUser: boolean }>(
      '/api/auth/apple',
      data
    ),
};

// Profile API
export const profileApi = {
  get: () =>
    api.get<OwnProfileResponse>('/api/profile'),

  update: (data: {
    first_name?: string;
    birth_date?: string;
    gender_id?: number;
    relationship_status_id?: number;
    bio?: string;
    city?: string;
    state?: string;
    country?: string;
    school?: string;
    company?: string;
    job_title?: string;
    height_cm?: number;
    latitude?: number;
    longitude?: number;
    profile_json?: Partial<ProfileJson>;
  }) =>
    api.put<OwnProfileResponse>('/api/profile', data),

  checkName: (name: string) =>
    api.get<{ available: boolean; suggestion: string | null }>('/api/profile/check-name', { params: { name } }),

  getById: (userId: string) =>
    api.get<PublicProfileResponse>(`/api/profile/${userId}`),

  changeEmail: (email: string) =>
    api.post<{ pending: boolean; email: string }>('/api/profile/email/change', { email }),

  verifyEmail: (email: string, code: string) =>
    api.post<{ success: boolean; email: string }>('/api/profile/email/verify', { email, code }),

  updatePhone: (phone_number: string | null) =>
    api.put<{ success: boolean; phone_number: string | null }>('/api/profile/phone', { phone_number }),

  updateHandle: (handle: string) =>
    api.put<{ success: boolean; handle: string }>('/api/profile/handle', { handle }),
};

// Preferences API
export const preferencesApi = {
  get: () =>
    api.get<PreferencesResponse>('/api/preferences'),

  update: (data: PreferencesUpdateData) =>
    api.put<PreferencesResponse>('/api/preferences', data),

  updateVisibility: (data: Partial<VisiJson>) =>
    api.put<VisibilityResponse>('/api/preferences/visibility', data),
};

// Search API (for browse grid)
export const searchApi = {
  browse: (params: SearchParams = {}) => {
    // Serialize intent_entries as JSON string (complex nested objects can't be query-param encoded)
    const serialized: Record<string, any> = { ...params };
    if (serialized.intent_entries) {
      serialized.intent_entries = JSON.stringify(serialized.intent_entries);
    }
    return api.get<SearchResult>('/api/search', { params: serialized });
  },

  interact: (user_id_to: string, score: number, comment?: string) =>
    api.post<{ isMatch: boolean; chatId?: string }>(
      '/api/search/interact',
      { user_id_to, score, comment }
    ),

  getLikesCount: () =>
    api.get<{ count: number }>('/api/search/likes-count'),
};

// Matches API
export const matchesApi = {
  getAll: () =>
    api.get<Match[]>('/api/matches'),

  checkMatch: (userId: string) =>
    api.get<MatchCheckResult>(`/api/matches/check/${userId}`),

  getUnreadCount: () =>
    api.get<{ count: number }>('/api/matches/unread-count'),
};

// Chats API
export const chatsApi = {
  getAll: () =>
    api.get<Chat[]>('/api/chats'),

  create: (userId: string) =>
    api.post<ChatCreateResponse>('/api/chats', { user_id: userId }),

  getById: (chatId: string) =>
    api.get<ChatDetailResponse>(`/api/chats/${chatId}`),

  getMessages: (chatId: string, before?: string) =>
    api.get<Message[]>(`/api/chats/${chatId}/messages`, { params: { before } }),

  sendMessage: (chatId: string, content?: string, mediaIds?: string[]) => {
    const body: { content?: string; media_ids?: string[] } = {};
    if (content) body.content = content;
    if (mediaIds && mediaIds.length > 0) body.media_ids = mediaIds;
    return api.post<Message>(`/api/chats/${chatId}/messages`, body);
  },

  endChat: (chatId: string) =>
    api.put<{ success: boolean }>(`/api/chats/${chatId}/end`),

  clearChat: (chatId: string) =>
    api.delete<{ success: boolean }>(`/api/chats/${chatId}/messages`),

  logView: (chatId: string) =>
    api.post<{ success: boolean }>(`/api/chats/${chatId}/view`),

  requestIcebreaker: (chatId: string) =>
    api.post<{ success: boolean }>(`/api/chats/${chatId}/icebreaker`),

  uploadChatImage: (chatId: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post<ChatImageUploadResponse>(`/api/chats/${chatId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getSentImages: (chatId: string) =>
    api.get<SentImage[]>(`/api/chats/${chatId}/sent-images`),

  deleteChatImages: (chatId: string, mediaIds: string[]) =>
    api.delete<{ success: boolean }>(`/api/chats/${chatId}/sent-images`, { data: { mediaIds } }),

  messageAction: (chatId: string, messageId: string, action: 'accept' | 'reject') =>
    api.post<{ success: boolean }>(`/api/chats/${chatId}/messages/${messageId}/message-action`, { action }),

  getImageTrust: (chatId: string) =>
    api.get<{ trusted: boolean }>(`/api/chats/${chatId}/image-trust`),

  setImageTrust: (chatId: string, trusted: boolean) =>
    api.put<{ success: boolean; trusted: boolean }>(`/api/chats/${chatId}/image-trust`, { trusted }),

  untrust: (chatId: string) =>
    api.put<{ success: boolean }>(`/api/chats/${chatId}/untrust`),
};

// Genders API (for preferences)
export const gendersApi = {
  getAll: () =>
    api.get<{ id: number; name: string; description: string }[]>('/api/genders'),
};

// Relationship Statuses API
export const relationshipStatusesApi = {
  getAll: () =>
    api.get<{ id: number; name: string; description: string }[]>('/api/profile/relationship-statuses/all'),
};

// Media API
export const mediaApi = {
  // Upload a new photo
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post<ProfilePhoto>('/api/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Get current user's photos
  getPhotos: () =>
    api.get<ProfilePhoto[]>('/api/media/photos'),

  // Get another user's photos
  getPhotosByUserId: (userId: string) =>
    api.get<ProfilePhoto[]>(`/api/media/photos/${userId}`),

  // Get current user's avatar URL
  getAvatar: () =>
    api.get<{ avatar_url: string | null }>('/api/media/avatar'),

  // Get another user's avatar URL
  getAvatarByUserId: (userId: string) =>
    api.get<{ avatar_url: string | null }>(`/api/media/avatar/${userId}`),

  // Reorder photos (change sort_order)
  reorder: (mediaId: string, sortOrder: number) =>
    api.put<ProfilePhoto[]>(`/api/media/${mediaId}/reorder`, { sort_order: sortOrder }),

  // Update caption
  updateCaption: (mediaId: string, caption: string) =>
    api.put<ProfilePhoto>(`/api/media/${mediaId}/caption`, { caption }),

  // Delete a photo
  delete: (mediaId: string) =>
    api.delete<ProfilePhoto[]>(`/api/media/${mediaId}`),

  // Update crop for a photo
  updateCrop: (mediaId: string, crop: CropData) =>
    api.put<ProfilePhoto[]>(`/api/media/${mediaId}/crop`, { crop }),
};

// Admin API
export const adminApi = {
  deleteUser: (userId: string) =>
    api.delete<{ success: boolean }>(`/api/auth/admin/delete-user/${userId}`),
  overview: () =>
    api.get<any>('/api/admin/overview'),
  moderation: (params: Record<string, any>) =>
    api.get<any>('/api/admin/moderation', { params }),
  users: (params: Record<string, any>) =>
    api.get<any>('/api/admin/users', { params }),
  reports: (params: Record<string, any>) =>
    api.get<any>('/api/admin/reports', { params }),
  updateReportStatus: (id: string, status: string) =>
    api.put<any>(`/api/admin/reports/${id}/status`, { status }),
  blocks: (params: Record<string, any>) =>
    api.get<any>('/api/admin/blocks', { params }),
  stats: (days: number) =>
    api.get<any>('/api/admin/stats', { params: { days } }),
};

// Q&A API
export const qaApi = {
  listSets: (params?: { sort?: string; sort_dir?: string; category_id?: string }) =>
    api.get<{ sets: any[]; has_more: boolean }>('/api/qa/sets', { params }),

  getSet: (setId: string) =>
    api.get<any>(`/api/qa/sets/${setId}`),

  getSetQuestions: (setId: string) =>
    api.get<any[]>(`/api/qa/sets/${setId}/questions`),

  getMyAnswersForSet: (setId: string) =>
    api.get<any[]>(`/api/qa/answers/mine/set/${setId}`),

  submitAnswer: (data: { question_id: string; answer_text?: string; answer_number?: number; answer_date?: string; selected_choice_ids?: string[]; other_text?: string; comment_text?: string }) =>
    api.post<{ answer: any; version: any; comment_rejected?: boolean }>('/api/qa/answers', data),

  editAnswer: (answerId: string, data: { answer_text?: string; answer_number?: number; answer_date?: string; selected_choice_ids?: string[]; other_text?: string; comment_text?: string }) =>
    api.put<{ answer: any; version: any; comment_rejected?: boolean }>(`/api/qa/answers/${answerId}`, data),

  deleteAnswer: (answerId: string) =>
    api.delete(`/api/qa/answers/${answerId}`),

  getPairwiseSets: (otherUserId: string) =>
    api.get<any[]>(`/api/qa/pairwise/sets/${otherUserId}`),

  getPairwiseAnswers: (setId: string, otherUserId: string) =>
    api.get<{ set: any; questions: any[]; ai_answering?: { name: string } }>(`/api/qa/pairwise/${setId}/${otherUserId}`),
};

export const blocksApi = {
  block: (blocked_user_id: string, reason?: string) =>
    api.post('/api/blocks', { blocked_user_id, reason }),
  unblock: (blocked_user_id: string) =>
    api.delete(`/api/blocks/${blocked_user_id}`),
};

export const reportsApi = {
  report: (target_user_id: string, category: string, description?: string) =>
    api.post('/api/reports', { target_user_id, category, description }),
  getCategories: () =>
    api.get<string[]>('/api/reports/categories'),
};

// Notification API
export const notificationsApi = {
  registerToken: (token: string, platform: 'ios' | 'android') =>
    api.post<{ success: boolean }>('/api/notifications/token', { token, platform }),

  deactivateToken: (token: string) =>
    api.delete<{ success: boolean }>('/api/notifications/token', { data: { token } }),

  getPreferences: () =>
    api.get<Record<string, any>>('/api/notifications/preferences'),

  updatePreferences: (prefs: Record<string, any>) =>
    api.put<Record<string, any>>('/api/notifications/preferences', prefs),
};

// Invites API
export const invitesApi = {
  send: (emails: string[]) =>
    api.post<{ sent: number; matched: number; skipped: number; errors: string[] }>('/api/invites', { emails }),
};

// AI Editor API (admin)
export const aiEditorApi = {
  // Knowledge
  listSections: (userId: string) => api.get(`/api/admin/knowledge/${userId}`),
  createSection: (userId: string, data: any) => api.post(`/api/admin/knowledge/${userId}`, data),
  updateSection: (userId: string, id: string, data: any) => api.put(`/api/admin/knowledge/${userId}/${id}`, data),
  deleteSection: (userId: string, id: string) => api.delete(`/api/admin/knowledge/${userId}/${id}`),
  reorderSections: (userId: string, ids: string[]) => api.put(`/api/admin/knowledge/${userId}/reorder`, { ids }),
  // Personality
  getPersonality: (userId: string) => api.get(`/api/admin/personality/${userId}`),
  savePersonality: (userId: string, data: any) => api.put(`/api/admin/personality/${userId}`, data),
  // Facts
  listFacts: (userId: string) => api.get(`/api/admin/facts/${userId}`),
  createFact: (userId: string, data: any) => api.post(`/api/admin/facts/${userId}`, data),
  updateFact: (userId: string, id: string, data: any) => api.put(`/api/admin/facts/${userId}/${id}`, data),
  deleteFact: (userId: string, id: string) => api.delete(`/api/admin/facts/${userId}/${id}`),
};

export default api;
